import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { CogsAdditionalAddressDto, CogsContactDto, CogsDto, CogsInvoiceAddressDto, CogsPersonDto, CogsServiceProxy, CogsStatus, CreateCogsApplicationDto, CreateOrUpdateCogsAdditionalAddressInput, CreateOrUpdateCogsContactInput, CreateOrUpdateCogsInvoiceAddressInput, ExternalSystemEnum, GetCogsOutput, HealthRegisterIdentificationStatus, PersonApplicationSummaryDto } from '../../../shared/service-proxies/service-proxies';
import { delay, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { SelectItem } from '@node_modules/primeng/api';
import { AppConsts } from '@shared/AppConsts';

@Injectable({providedIn: 'root'})
export class CogsService extends AppComponentBase {

    cogs: CogsDto;
    onlyPrivateAddressAllowed = false;

    private cogsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>();
    readonly cogsForPerson$ = this.cogsForPersonSubject.asObservable();


    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private cogsService: CogsServiceProxy
    ) {
        super(injector);
        console.log('CogsService constructor');
    }

    loadCogsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.cogsService.getCogsForPerson(gln).pipe(
                tap(cogs => this.cogsForPersonSubject.next(cogs))
            );
        } else {
            this.cogsForPersonSubject.next([]);
            return of([]);
        }
    }

    createCogs(input: CreateCogsApplicationDto): Observable<string> {
        this.closeCogs();
        return this.cogsService.createCogs(input).pipe();
    }

    getCogs(caseId: string): Observable<GetCogsOutput> {
        this.closeCogs();
        return this.cogsService.getCogs(caseId).pipe(
            tap((output: GetCogsOutput) => {
                this.cogs = output.cogs;
            })
        );
    }

    closeCogs(): void {
        this.cogs = null;
    }

    getNextStatus(status: CogsStatus): Observable<CogsStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.cogsService.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority && status === CogsStatus.Release) {
                return CogsStatus.FinishReview;
            }
            return status;
        }));
    }

    getPerson(): Observable<CogsPersonDto> {
        return of(this.cogs.cogsPerson).pipe(delay(1));
    }

    releaseCogs(comment: string): Observable<void> {
        return this.cogsService.releaseCogs(this.caseId, comment);
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.cogsService.getExternalRegisterByProfessionId(professionId);
    }

    updatePersonWithRegisterCheck(gln: string): Observable<HealthRegisterIdentificationStatus>{
        return this.cogsService.updateCogsPersonWithRegisterCheck(this.caseId, gln).pipe();
    }

    acceptRegisterCheck(): Observable<CogsStatus> {
        this.applicationService.setStepToReview(CogsStatus.Person);
        return this.cogsService.acceptRegisterCheck(this.caseId).pipe(
            tap((status: CogsStatus) => {
                this.cogs.currentStep = status;
            })
        );
    }

    getMyCogs(): Observable<PersonApplicationSummaryDto[]> {
        return this.cogsService.getMyCogss();
    }

    getStepUrl(nextStep: CogsStatus): string {
        return this.applicationService.getNextStep(CogsService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: CogsStatus): string {
        switch (step) {
            case CogsStatus.FinishReview:
                return 'finish-review';
            case CogsStatus.Person:
                return 'person';
            case CogsStatus.Contact:
                return 'contact';
            case CogsStatus.Language:
                return 'language';
            case CogsStatus.AdditionalAddressOfService:
                return 'additional-address';
            case CogsStatus.InvoiceAddress:
                return 'invoice-address';
            case CogsStatus.Release:
                return 'release';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    setCogsPersonStepToReview(){
        this.applicationService.setStepToReview(CogsStatus.Person);
    }

    get caseId(): string {
        return this.cogs?.application?.caseId;
    }

    getCogsContact(): Observable<CogsContactDto> {
        return of(this.cogs.cogsContact).pipe(delay(0));
    }

    getDossierContact(): Observable<CogsContactDto> {
        if (this.cogs?.cogsPerson?.gln){
            return this.cogsService.getDossierContact(this.cogs.cogsPerson.gln).pipe();
        } else {
            return of(new CogsContactDto()).pipe(delay(0));
        }
    }

    createOrUpdateCogsContact(input: CreateOrUpdateCogsContactInput): Observable<void> {
        this.applicationService.setStepToReview(CogsStatus.Contact);
        return this.cogsService.createOrUpdateCogsContact(input).pipe(tap((status: CogsStatus) => {
            this.cogs.cogsContact = input.cogsContact;
            this.cogs.currentStep = status;
        })).pipe(map((status: CogsStatus) => null));
    }

    updateLanguageOfCogs(languageOfCogsId: string): Observable<void> {
        return this.cogsService.updateLanguageOfCogs(this.caseId, languageOfCogsId).pipe(
            tap((status: CogsStatus) => {
                this.cogs.languageOfCogsId = languageOfCogsId;
                this.cogs.currentStep = status;
            })
        ).pipe(map((status: CogsStatus) => null));
    }

    getAdditionalAddress(): Observable<CogsAdditionalAddressDto> {
        return of(this.cogs.cogsAdditionalAddress).pipe(delay(0));
    }

    createOrUpdateCogsAdditionalAddress(input: CreateOrUpdateCogsAdditionalAddressInput ): Observable<void> {
        this.applicationService.setStepToReview(CogsStatus.AdditionalAddressOfService);
        return this.cogsService.createOrUpdateCogsAdditionalAddress(input).pipe(tap((status: CogsStatus) => {
            this.cogs.cogsAdditionalAddress = input.cogsAdditionalAddress;
            this.cogs.currentStep = status;
        })).pipe(map((status: CogsStatus) => null));
    }

    createAddressDropdownList(): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.cogs.cogsContact) });

        if (!this.onlyPrivateAddressAllowed) {

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: CogsContactDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    getCogsInvoiceAddress(): Observable<CogsInvoiceAddressDto> {
        if (this.cogs.cogsInvoiceAddress.typeOfAddressId === AppConsts.codes.address.private){
            return of(new CogsInvoiceAddressDto({
                typeOfAddressId: AppConsts.codes.address.private,
                firstName: this.cogs.cogsPerson.firstName,
                lastName: this.cogs.cogsPerson.lastName,
                street: this.cogs.cogsContact.street,
                streetNr: this.cogs.cogsContact.streetNr,
                postOfficeBox: this.cogs.cogsContact.postOfficeBox,
                addressAddition: null,
                postCode: this.cogs.cogsContact.postCode,
                city: this.cogs.cogsContact.city,
                countryId: this.cogs.cogsContact.countryId,
                email: this.cogs.cogsContact.email1,
                completionStatus: null
            })).pipe(delay(0));
        } else {
            return of(this.cogs.cogsInvoiceAddress).pipe(delay(0));
        }
    }

    createOrUpdateCogsInvoiceAddress(input: CreateOrUpdateCogsInvoiceAddressInput): Observable<void> {
        this.applicationService.setStepToReview(CogsStatus.InvoiceAddress);
        return this.cogsService.createOrUpdateCogsInvoiceAddress(input).pipe(tap((status: CogsStatus) => {
            this.cogs.cogsInvoiceAddress = input.cogsInvoiceAddress;
            this.cogs.currentStep = status;
        }))
            .pipe(map((status: CogsStatus) => null));
    }

    getEvaluationComments(): Observable<string> {
        return this.cogsService.getEvaluationComments(this.applicationService.caseId);
    }

    getPersonId(): Observable<number> {
        return this.cogsService.getPersonId(this.applicationService.caseId).pipe(delay(0));
    }
}
